// =============================================================================
// ZONE EDITOR 3D - ZONE POLYGON
// =============================================================================

import { useMemo } from 'react'
import * as THREE from 'three'
import { Html } from '@react-three/drei'
import { mmToSceneScale } from './utils'
import type { Zone } from '../../types'

interface ZonePolygonProps {
  zone: Zone
  isSelected: boolean
  onClick: () => void
  colorIndex: number
}

// High-contrast colors that stand out against dark green surface
const ZONE_COLORS = [
  0xff4444, // bright red
  0x4488ff, // bright blue
  0xff44ff, // magenta
  0xffaa00, // orange
  0xff6688, // pink
  0xaa55ff, // purple
  0x00ddff, // cyan
  0xffff44, // yellow
]

const ZONE_HEIGHT = 0.03 // 30mm - visible from distance

export function ZonePolygon({ zone, isSelected, onClick, colorIndex }: ZonePolygonProps) {
  const points = zone.geometry_points
  if (!points || points.length < 3) return null

  const { shape, center } = useMemo(() => {
    const s = new THREE.Shape()
    // Shape is in XY plane; after -PI/2 X rotation: shape X→world X, shape Y→world -Z
    // We want world Z = -worldY/1000, so shape Y = worldY/1000 (positive)
    const scenePoints = points.map(p => ({
      sx: mmToSceneScale(p.x),
      sy: mmToSceneScale(p.y)  // positive: after rotation becomes -Z = correct
    }))

    s.moveTo(scenePoints[0].sx, scenePoints[0].sy)
    for (let i = 1; i < scenePoints.length; i++) {
      s.lineTo(scenePoints[i].sx, scenePoints[i].sy)
    }
    s.closePath()

    // Center in world coordinates (for Html label which is NOT rotated)
    const cx = scenePoints.reduce((sum, p) => sum + p.sx, 0) / scenePoints.length
    const worldZ = -(scenePoints.reduce((sum, p) => sum + p.sy, 0) / scenePoints.length)

    return { shape: s, center: [cx, worldZ] as [number, number] }
  }, [points])

  // Always use index-based colors (led_color is often white which looks bad in 3D)
  const color = ZONE_COLORS[colorIndex % ZONE_COLORS.length]
  const extrudeHeight = isSelected ? ZONE_HEIGHT * 3 : ZONE_HEIGHT

  return (
    <group>
      {/* Zone surface - extruded polygon */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0.003, 0]}
        onClick={(e) => {
          e.stopPropagation()
          onClick()
        }}
      >
        <extrudeGeometry args={[shape, {
          depth: extrudeHeight,
          bevelEnabled: false
        }]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={isSelected ? 0.85 : 0.65}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Top face outline */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, extrudeHeight + 0.004, 0]}
      >
        <shapeGeometry args={[shape]} />
        <meshBasicMaterial color={color} wireframe transparent opacity={0.8} />
      </mesh>

      {/* Zone name label - fixed size */}
      <Html
        position={[center[0], extrudeHeight + 0.02, center[1]]}
        center
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        <div style={{
          background: isSelected ? 'rgba(59,130,246,0.85)' : 'rgba(0,0,0,0.7)',
          color: '#fff',
          padding: '1px 6px',
          borderRadius: 3,
          fontSize: 10,
          whiteSpace: 'nowrap',
          fontFamily: 'sans-serif',
          border: isSelected ? '1px solid rgba(255,255,255,0.5)' : '1px solid rgba(255,255,255,0.15)'
        }}>
          {zone.name}
        </div>
      </Html>
    </group>
  )
}
